import { figtree, fraunces, readingSerif } from '../../Landing/fonts';

// Reading column with editorial typography. Keeps the legacy `postcontent`
// and `postanchors` classes so existing global CSS for embeds, anchors and
// galleries keeps working; `tf-article` layers the new type system on top.
//
// Width system (three tiers, widest wins):
//   1. text + portraits      — 46rem column
//   2. .tf-wide / .fullwidth — min(92vw, 64rem), the hero width; full-bleed
//                              on phones (landscape photos, videos, galleries)
//   3. hero                  — same 64rem, framed in ArticleHero
export default function ArticleBody({ children }) {
  return (
    <div className="tw-mx-auto tw-max-w-[46rem] tw-px-4">
      <div className="tf-article postcontent postanchors textPrimary">
        {children}
      </div>
      <style jsx global>{`
        .tf-article {
          font-family: ${readingSerif.style.fontFamily};
          font-size: 1.0625rem;
          line-height: 1.75;
          word-break: break-word;
          color: #243630;
        }
        .tw-dark .tf-article {
          color: #cfdad2;
        }
        @media (min-width: 576px) {
          .tf-article {
            font-size: 1.1875rem;
          }
        }
        .tf-article ::selection {
          background: rgba(0, 150, 136, 0.22);
        }
        /* Editorial drop cap on the opening paragraph */
        .tf-article > p:first-child::first-letter,
        .tf-article > p:nth-child(-n + 3):first-of-type::first-letter {
          font-family: ${fraunces.style.fontFamily};
          font-weight: 600;
          font-size: 3.6em;
          line-height: 0.78;
          float: left;
          padding-right: 0.14em;
          padding-top: 0.05em;
          color: #00796b;
        }
        .tw-dark .tf-article > p:first-child::first-letter,
        .tw-dark .tf-article > p:nth-child(-n + 3):first-of-type::first-letter {
          color: #80cbc4;
        }
        .tf-article p,
        .tf-article .tf-flow {
          margin: 0 0 1.35em;
        }
        .tf-article h1,
        .tf-article h2,
        .tf-article h3,
        .tf-article h4 {
          font-family: ${fraunces.style.fontFamily};
          font-weight: 600;
          letter-spacing: -0.01em;
          line-height: 1.22;
          margin: 1.7em 0 0.55em;
          color: #1d2d28;
        }
        .tw-dark .tf-article h1,
        .tw-dark .tf-article h2,
        .tw-dark .tf-article h3,
        .tw-dark .tf-article h4 {
          color: #ebe6d9;
        }
        .tf-article h1,
        .tf-article h2 {
          font-size: 2rem;
        }
        .tf-article h3 {
          font-size: 1.55rem;
        }
        .tf-article h4 {
          font-size: 1.25rem;
        }
        /* Links: owned by the legacy .postcontent rules in style.css
           (teal + gradient-underline hover) — defining them here too made
           the result depend on stylesheet order. */
        .tf-article blockquote {
          border: 0;
          margin: 2em 0;
          padding: 0 0 0 1.6em;
          position: relative;
          font-family: ${fraunces.style.fontFamily};
          font-style: italic;
          font-size: 1.18em;
          line-height: 1.55;
          color: rgba(29, 45, 40, 0.78);
        }
        .tf-article blockquote::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0.25em;
          bottom: 0.25em;
          width: 3px;
          border-radius: 3px;
          background: linear-gradient(#00897b, rgba(0, 137, 123, 0.25));
        }
        .tw-dark .tf-article blockquote {
          color: rgba(235, 230, 217, 0.82);
        }

        /* ——— images, figures and the wide breakout ——— */
        .tf-article figure {
          margin: 2.2em auto;
        }
        .tf-article img {
          border-radius: 12px;
        }
        /* Promo-card art (official store badges, mock-browser covers)
           carries its own borders/frames — the article radius crops them. */
        .tf-article .tf-inline-promo img {
          border-radius: 0;
        }
        .tf-article figcaption {
          margin: 0.9em auto 0;
          max-width: 36rem;
          text-align: center;
          font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
          font-size: 0.6875rem;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.16em;
          line-height: 1.6;
          color: #8a958f;
        }
        /* Breakout width + horizontal shadow-fade room as variables so
           the 1500px rail override only swaps the width. --tf-fade-x is
           capped by the viewport leftover (92vw + 2 * 3vw ≤ 98vw), so
           the widened clip boxes can never overflow into a horizontal
           scrollbar; the shadow needs 25px (55px blur − 30px spread), so
           from ~933px up the fade renders in full and below that the cut
           lands where the shadow is already imperceptible. */
        .tf-article {
          --tf-breakout: min(92vw, 64rem);
          --tf-fade-x: min(3vw, 1.75rem);
        }
        /* content-visibility forces paint containment, which clips
           descendants at the figure's padding edge — the wide-photo drop
           shadow was being cut off mid-fade (a hard-edged band under
           captioned photos, no shadow at all under captionless ones).
           Reserve the fade zone inside the clip box and give the space
           back from the figure margin. */
        .tf-article .tf-wide {
          padding-bottom: 3.5rem;
          margin-bottom: calc(2.2em - 3.5rem);
        }
        .tf-article .tf-wide figcaption {
          margin-top: 1.6rem;
        }
        .tf-article .tf-wide {
          width: 100vw;
          margin-left: calc(50% - 50vw);
          margin-right: 0;
        }
        .tf-article .tf-wide img {
          width: 100%;
          height: auto;
          border-radius: 0;
        }
        @media (min-width: 640px) {
          .tf-article .tf-wide {
            width: var(--tf-breakout);
            margin-left: calc((100% - var(--tf-breakout)) / 2);
          }
          /* Photo figures also reserve the fade zone sideways: the box
             widens by the padding and the margin re-centers the photo,
             so the visible photo width is unchanged. Embeds
             (div.tf-embed.tf-wide) must NOT get this — their inset-0
             iframe fills the padding box, so side padding would widen
             the visible frame; their shadow sits on the contained
             element itself, which paint containment never clips. */
          .tf-article figure.tf-wide {
            width: calc(var(--tf-breakout) + 2 * var(--tf-fade-x));
            margin-left: calc(
              (100% - var(--tf-breakout)) / 2 - var(--tf-fade-x)
            );
            padding-left: var(--tf-fade-x);
            padding-right: var(--tf-fade-x);
          }
          .tf-article .tf-wide img {
            border-radius: 14px;
            box-shadow: 0 30px 55px -30px rgba(29, 45, 40, 0.45);
          }
        }
        /* Legacy gallery/TOC breakout class — align it with tf-wide.
           The legacy class provided vertical spacing via padding, which
           the breakout zeroes — restore it as margins. */
        .tf-article .fullwidth {
          width: 100vw !important;
          max-width: 100vw !important;
          margin-left: calc(50% - 50vw) !important;
          margin-right: 0 !important;
          margin-top: 2.2em !important;
          /* bottom padding reserves shadow-fade room inside the
             contain:paint clip (same trick as .tf-wide); the margin
             gives the space back so net spacing is unchanged */
          margin-bottom: calc(2.2em - 3.5rem) !important;
          padding: 0 0 3.5rem;
        }
        /* The carousel's overflow scroller does its own clipping —
           give the room to the scroller instead of the wrapper. */
        .tf-article .fullwidth.tw-relative {
          padding-bottom: 0;
        }
        .tf-article .fullwidth .tw-snap-x {
          padding-bottom: 3.5rem;
        }
        /* Galleries cast the editorial shadow per PHOTO, exactly like
           .tf-wide images: carousel slides on the image itself, masonry
           tiles and SSR-strip cells via .tf-tile. NEVER on a wrapper
           around the tiles: box-shadow doesn't paint under its own box,
           so a wrapper shadow renders as a hard-edged uniform slab that
           starts abruptly at the photos' bottom edge and bridges the
           gaps between tiles — a gray band, not a shadow. Phones are
           full-bleed (no radius, no shadow), same as .tf-wide. */
        @media (min-width: 640px) {
          .tf-article .fullwidth .tw-snap-x img {
            border-radius: 14px;
            box-shadow: 0 30px 55px -30px rgba(29, 45, 40, 0.45);
          }
          .tf-article .fullwidth .tf-tile {
            border-radius: 0.5rem;
            box-shadow: 0 30px 55px -30px rgba(29, 45, 40, 0.45);
          }
        }
        @media (min-width: 640px) {
          .tf-article .fullwidth {
            width: var(--tf-breakout) !important;
            max-width: var(--tf-breakout) !important;
            margin-left: calc((100% - var(--tf-breakout)) / 2) !important;
          }
          /* Masonry + SSR-strip wrappers get the sideways fade zone too
             (the sheet shadow was clipping in a hard vertical edge).
             Scoped to .tf-gallery-block, NOT all .fullwidth: the
             carousel's overflow scroller clips slide shadows at its own
             box regardless (and its absolute arrows hang off the
             wrapper's padding box), and TravelResources paints a colored
             card on the .fullwidth element itself, which side padding
             would visibly widen. */
          .tf-article .tf-gallery-block {
            width: calc(var(--tf-breakout) + 2 * var(--tf-fade-x)) !important;
            max-width: calc(
              var(--tf-breakout) + 2 * var(--tf-fade-x)
            ) !important;
            margin-left: calc(
              (100% - var(--tf-breakout)) / 2 - var(--tf-fade-x)
            ) !important;
            padding-left: var(--tf-fade-x);
            padding-right: var(--tf-fade-x);
          }
        }
        /* Very wide screens: the conversion rail takes over from the
           in-article promo cards, and breakouts cap slightly narrower so
           photos never slide under the rail — every width/margin rule
           tracks the variable. */
        @media (min-width: 1500px) {
          /* The rail shows the blog pitch at this width */
          .tf-article .tf-inline-promo:not(.tf-promo-menu) {
            display: none;
          }
          .tf-article {
            --tf-breakout: min(92vw, 54rem);
          }
        }
        /* The in-post Menu Translator card hides only when the rail's menu
           card is actually visible (wide AND tall enough — short viewports
           shed it from the rail to keep the sidebar from being cut off). */
        @media (min-width: 1500px) and (min-height: 1180px) {
          .tf-article .tf-promo-menu {
            display: none;
          }
        }

        /* ——— offscreen media stays cold ———
           Image-heavy posts (the worst is 300k px tall with 400+ photos)
           crash iOS Safari when momentum scrolling decodes images faster
           than WebKit purges them. content-visibility keeps offscreen
           media blocks unpainted (and their bitmaps undecoded); the
           intrinsic-size estimate keeps scroll geometry stable and is
           replaced by the real size once a block has rendered. Browsers
           without support simply ignore both. Headings/anchors stay
           outside these blocks, so TOC jumps still land. */
        .tf-article figure,
        .tf-article .fullwidth,
        .tf-article .tf-embed {
          content-visibility: auto;
          contain-intrinsic-size: auto 320px;
        }

        /* ——— video and rich embeds ——— */
        .tf-article .tf-embed {
          position: relative;
          margin-top: 2.2em;
          margin-bottom: 2.2em;
          overflow: hidden;
          background: rgba(29, 45, 40, 0.06);
        }
        .tw-dark .tf-article .tf-embed {
          background: rgba(235, 230, 217, 0.06);
        }
        @media (min-width: 640px) {
          .tf-article .tf-embed {
            border-radius: 14px;
            box-shadow: 0 30px 55px -30px rgba(29, 45, 40, 0.45);
          }
        }
        .tf-article .tf-embed .lazyload-wrapper {
          position: absolute;
          inset: 0;
        }
        .tf-article .tf-embed iframe {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          border: 0;
          display: block;
        }
        .tf-article .tf-embed-block {
          margin: 2.2em 0;
        }
        .tf-article .tf-embed-block iframe {
          width: 100%;
          max-width: 100%;
          border: 0;
          border-radius: 12px;
        }

        /* ——— dividers, lists, tables, code ——— */
        .tf-article hr {
          border: 0;
          margin: 3em 0;
          text-align: center;
        }
        .tf-article hr::after {
          content: '✈';
          display: inline-block;
          font-size: 1rem;
          color: rgba(0, 137, 123, 0.55);
          letter-spacing: 1em;
          padding-left: 1em;
        }
        .tf-article ul,
        .tf-article ol {
          margin: 0 0 1.35em;
          padding-left: 1.4em;
        }
        .tf-article li {
          margin-bottom: 0.45em;
        }
        .tf-article li::marker {
          color: #00897b;
        }
        .tf-article table {
          width: 100%;
          margin: 2em 0;
          border-collapse: collapse;
          font-family: ${figtree.style.fontFamily}, sans-serif;
          font-size: 0.82em;
          line-height: 1.5;
        }
        .tf-article table th,
        .tf-article table td {
          border: 1px solid rgba(128, 128, 128, 0.22);
          padding: 0.6em 0.9em;
          text-align: left;
        }
        .tf-article table th {
          background: rgba(0, 150, 136, 0.07);
        }

        /* ——— guide tables (.tf-table-block, markup from GuideTable) ———
           A paper-card ledger at every width. 640px+ it breaks out of the
           text column to the tf-wide width (tracks --tf-breakout, so the
           1500px rail narrowing applies automatically); on phones it
           stays a real table — comparison is the point — scrolling
           sideways inside the card with the first column stuck so you
           always see which row you're comparing. */
        .tf-article .tf-table-block {
          margin: 2.4em 0;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          background: #ffffff;
          border: 1px solid #e7e0cf;
          border-radius: 14px;
          box-shadow: 0 24px 50px -24px rgba(29, 45, 40, 0.35);
        }
        .tw-dark .tf-article .tf-table-block {
          background: #15231e;
          border-color: #26382f;
        }
        .tf-article .tf-table-block table {
          width: 100%;
          margin: 0;
          border-collapse: separate;
          border-spacing: 0;
          font-size: 0.92rem;
          line-height: 1.55;
        }
        .tf-article .tf-table-block th,
        .tf-article .tf-table-block td {
          border: 0;
          text-align: left;
          vertical-align: top;
          padding: 0.75em 1.1em;
          /* The article's break-word would split "WordPress.org" —
             columns have min-widths instead */
          word-break: normal;
          overflow-wrap: normal;
        }
        .tf-article .tf-table-block th:first-child,
        .tf-article .tf-table-block td:first-child {
          padding-left: 1.35em;
        }
        .tf-article .tf-table-block th:last-child,
        .tf-article .tf-table-block td:last-child {
          padding-right: 1.35em;
        }
        .tf-article .tf-table-block thead th {
          font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
          font-size: 0.6875rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.14em;
          line-height: 1.5;
          color: #5f6f69;
          background: rgba(0, 150, 136, 0.06);
          border-bottom: 1px solid #e7e0cf;
          padding-top: 1em;
          padding-bottom: 0.9em;
        }
        .tw-dark .tf-article .tf-table-block thead th {
          color: #93a69e;
          background: rgba(77, 182, 172, 0.07);
          border-bottom-color: #26382f;
        }
        .tf-article .tf-table-block tbody td {
          border-bottom: 1px solid rgba(231, 224, 207, 0.65);
        }
        .tw-dark .tf-article .tf-table-block tbody td {
          border-bottom-color: rgba(38, 56, 47, 0.8);
        }
        .tf-article .tf-table-block tbody tr:last-child td {
          border-bottom: 0;
        }
        .tf-article .tf-table-block tbody td:first-child {
          font-weight: 600;
          color: #1d2d28;
        }
        .tw-dark .tf-article .tf-table-block tbody td:first-child {
          color: #ebe6d9;
        }
        /* TravelFeed highlight — teal wash on our row/column */
        .tf-article .tf-table-block tr.tf-row-us td,
        .tf-article .tf-table-block td.tf-col-us,
        .tf-article .tf-table-block thead th.tf-col-us {
          background: rgba(0, 150, 136, 0.055);
        }
        .tw-dark .tf-article .tf-table-block tr.tf-row-us td,
        .tw-dark .tf-article .tf-table-block td.tf-col-us,
        .tw-dark .tf-article .tf-table-block thead th.tf-col-us {
          background: rgba(77, 182, 172, 0.08);
        }
        .tf-article .tf-table-block tr.tf-row-us td:first-child {
          box-shadow: inset 3px 0 0 #00897b;
        }
        .tf-article .tf-table-block thead th.tf-col-us {
          color: #00695c;
          box-shadow: inset 0 3px 0 #00897b;
        }
        .tw-dark .tf-article .tf-table-block thead th.tf-col-us {
          color: #80cbc4;
          box-shadow: inset 0 3px 0 #4db6ac;
        }
        @media (min-width: 640px) {
          .tf-article .tf-table-block {
            width: max(100%, var(--tf-breakout));
            margin-left: calc((100% - max(100%, var(--tf-breakout))) / 2);
          }
          /* Columns never crush below readable — the block scrolls
             sideways instead (also covers many-column tables) */
          .tf-article .tf-table-block table {
            min-width: 36rem;
          }
        }
        @media (max-width: 639.98px) {
          .tf-article .tf-table-block {
            border-radius: 12px;
          }
          .tf-article .tf-table-block table {
            font-size: 0.85rem;
            line-height: 1.5;
          }
          .tf-article .tf-table-block th,
          .tf-article .tf-table-block td {
            padding: 0.65em 0.85em;
            min-width: 9.5rem;
          }
          /* Sticky row-label column: opaque backgrounds (they slide over
             the other cells), pre-blended where a wash sits on top */
          .tf-article .tf-table-block th:first-child,
          .tf-article .tf-table-block td:first-child {
            position: sticky;
            left: 0;
            z-index: 1;
            min-width: 6.75rem;
            max-width: 8.5rem;
            padding-left: 0.85em;
            background: #ffffff;
            border-right: 1px solid #ede6d6;
            box-shadow: 6px 0 10px -7px rgba(29, 45, 40, 0.25);
          }
          .tw-dark .tf-article .tf-table-block th:first-child,
          .tw-dark .tf-article .tf-table-block td:first-child {
            background: #15231e;
            border-right-color: #26382f;
          }
          .tf-article .tf-table-block thead th:first-child {
            background: #f0f9f8;
          }
          .tw-dark .tf-article .tf-table-block thead th:first-child {
            background: #192d28;
          }
          .tf-article .tf-table-block tr.tf-row-us td:first-child {
            background: #f1f9f8;
            box-shadow: inset 3px 0 0 #00897b,
              6px 0 10px -7px rgba(29, 45, 40, 0.25);
          }
          .tw-dark .tf-article .tf-table-block tr.tf-row-us td:first-child {
            background: #1a2f29;
          }
        }
        .tf-article pre,
        .tf-article code {
          background: rgba(128, 128, 128, 0.12);
          border-radius: 6px;
          font-size: 0.85em;
        }
        .tf-article code {
          padding: 0.15em 0.4em;
        }
        .tf-article pre {
          padding: 1em 1.25em;
          overflow-x: auto;
        }
        .tf-article pre code {
          background: none;
          padding: 0;
        }
      `}</style>
    </div>
  );
}
